import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import MultiSelectFilter from './MultiSelectFilter';
import SortDropdown from './SortDropdown';
import api from '../lib/api';
import { PAYMENT_MODE_OPTIONS, formatPaymentMode } from '../lib/utils';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkActionBar from './BulkActionBar';
import ConsolidatedReportDialog from './ConsolidatedReportDialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0].slice(-2)}`;
  return d;
};

const SIZE_ORDER = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL','Free Size','32','34','36','38','40','42'];

const SIZE_SETS = [
  { label: 'M to 2XL', sizes: ['M','L','XL','2XL'] },
  { label: '3XL to 5XL', sizes: ['3XL','4XL','5XL'] },
  { label: 'S to XL', sizes: ['S','M','L','XL'] },
  { label: 'S to 2XL', sizes: ['S','M','L','XL','2XL'] },
  { label: 'M to 5XL', sizes: ['M','L','XL','2XL','3XL','4XL','5XL'] },
  { label: 'Free Size', sizes: ['Free Size'] },
];

const sortColorWise = (variants) => {
  if (!variants || !variants.length) return [];
  const colorOrder = [];
  const colorMap = {};
  for (const v of variants) {
    if (!colorMap[v.color]) { colorMap[v.color] = []; colorOrder.push(v.color); }
    colorMap[v.color].push(v);
  }
  const sorted = [];
  for (const color of colorOrder) {
    colorMap[color].sort((a, b) => SIZE_ORDER.indexOf(a.size) - SIZE_ORDER.indexOf(b.size));
    sorted.push(...colorMap[color]);
  }
  return sorted;
};

const STATUS_COLORS = {
  confirmed: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
  partial_fulfilled: 'bg-amber-100 text-amber-700',
  fulfilled: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
  pending: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
  dispatched: 'bg-blue-100 text-blue-700',
  partial_dispatched: 'bg-amber-100 text-amber-700',
};

const APPROVAL_COLORS = {
  pending_approval: 'bg-orange-100 text-orange-800 border border-orange-300',
  approved: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
  rejected: 'bg-red-100 text-red-700 border border-red-300',
};

const approvalLabel = (s) => {
  const labels = { pending_approval: 'Pending Approval', approved: 'Approved', rejected: 'Rejected' };
  return labels[s] || s;
};

const SalesOrderManagement = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const bulk = useBulkSelect();
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [eventReportPickerOpen, setEventReportPickerOpen] = useState(false);
  const [reportEventId, setReportEventId] = useState('');
  const [downloadingFmt, setDownloadingFmt] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [products, setProducts] = useState([]);
  const [enums, setEnums] = useState({ sizes: [], colors: [] });
  const [brands, setBrands] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewingSO, setViewingSO] = useState(null);
  const [editingSO, setEditingSO] = useState(null);
  const [expandedSO, setExpandedSO] = useState(null);

  // Filters
  const [filterCustomers, setFilterCustomers] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filterOccasions, setFilterOccasions] = useState([]);
  const [filterEvents, setFilterEvents] = useState([]);
  const [filterAgents, setFilterAgents] = useState([]);
  const [filterDistributors, setFilterDistributors] = useState([]);
  const [filterPaymentModes, setFilterPaymentModes] = useState([]);
  const [filterCreditDays, setFilterCreditDays] = useState([]);
  const [filterCities, setFilterCities] = useState([]);
  const [filterStates, setFilterStates] = useState([]);
  const [filterStaff, setFilterStaff] = useState([]);
  const [searchSO, setSearchSO] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 200;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const searchTimerRef = useRef(null);

  // SO Creation
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const customerSearchRef = useRef(null);
  const [customerHistory, setCustomerHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [occasion, setOccasion] = useState('exhibition');
  const [eventId, setEventId] = useState('');
  const [eventInput, setEventInput] = useState('');
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [paymentMode, setPaymentMode] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedDistributor, setSelectedDistributor] = useState('');
  const [transportName, setTransportName] = useState('');
  const [dispatchDate, setDispatchDate] = useState('');
  const [soItems, setSoItems] = useState([]);
  const [soNotes, setSoNotes] = useState('');
  const [addMode, setAddMode] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [barcodeScanning, setBarcodeScanning] = useState(false);
  const productSearchRef = useRef(null);

  useEffect(() => { loadAllData(); }, []);

  // Close customer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customerSearchRef.current && !customerSearchRef.current.closest('.relative')?.contains(e.target)) {
        setCustomerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Resume SO creation after returning from customer creation
  const resumeDone = useRef(false);
  useEffect(() => {
    if (resumeDone.current || loading || !location.state?.fromCustomerCreate) return;
    resumeDone.current = true;
    const newCustId = location.state.newCustomerId;
    if (newCustId) {
      const cust = customers.find(c => c.id === newCustId);
      if (cust) {
        setSelectedCustomer(newCustId);
        setCustomerSearch(cust.company_name + (cust.city ? ` (${cust.city})` : ''));
        setSelectedAgent(cust.agent_id || '');
        setSelectedDistributor(cust.distributor_id || '');
        setTransportName(cust.transport_preference || '');
        setPaymentMode(cust.payment_mode || '');
        toast.success(`Customer "${cust.company_name}" selected — continue creating SO`);
      }
      setIsCreateOpen(true);
    }
    window.history.replaceState({}, document.title);
  }, [loading, location.state, customers]);

  const loadAllData = async () => {
    try {
      const [ordersRes, custRes, agentRes, distRes, prodRes, enumsRes, brandsRes, eventsRes] = await Promise.allSettled([
        api.get(`/sales-orders?page=1&limit=${PAGE_SIZE}`), api.get('/customers?include_inactive=true'), api.get('/agents'),
        api.get('/distributors'), api.get('/products'), api.get('/enums'), api.get('/brands'),
        api.get('/events'),
      ]);
      if (ordersRes.status === 'fulfilled') {
        setOrders(ordersRes.value.data);
        setTotalCount(parseInt(ordersRes.value.headers?.['x-total-count'] || ordersRes.value.data.length));
        setCurrentPage(1);
      }
      else { console.error('SO load failed:', ordersRes.reason); toast.error('Failed to load sales orders'); }
      if (custRes.status === 'fulfilled') setCustomers(custRes.value.data);
      if (agentRes.status === 'fulfilled') setAgents(agentRes.value.data);
      if (distRes.status === 'fulfilled') setDistributors(distRes.value.data);
      if (prodRes.status === 'fulfilled') setProducts(prodRes.value.data);
      if (enumsRes.status === 'fulfilled') setEnums(enumsRes.value.data);
      if (brandsRes.status === 'fulfilled') setBrands(brandsRes.value.data);
      if (eventsRes.status === 'fulfilled') setEvents(eventsRes.value.data);
      // Load drafts
      api.get('/drafts?type=SO').then(r => setDrafts(r.data || [])).catch(() => {});
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const loadFilteredOrders = async (page = 1, searchTerm = null) => {
    try {
      const params = new URLSearchParams();
      if (filterCustomers.length) params.append('customer_id', filterCustomers.join(','));
      if (filterStatuses.length) params.append('status', filterStatuses.join(','));
      if (filterOccasions.length) params.append('occasion', filterOccasions.join(','));
      if (filterEvents.length) params.append('event_id', filterEvents.join(','));
      if (filterAgents.length) params.append('agent_id', filterAgents.join(','));
      if (filterDistributors.length) params.append('distributor_id', filterDistributors.join(','));
      if (filterPaymentModes.length) params.append('payment_mode', filterPaymentModes.join(','));
      if (filterCities.length) params.append('city', filterCities.join(','));
      if (filterStates.length) params.append('state', filterStates.join(','));
      const q = searchTerm !== null ? searchTerm : searchSO;
      if (q && q.trim()) params.append('search', q.trim());
      params.append('page', page);
      params.append('limit', PAGE_SIZE);
      const res = await api.get(`/sales-orders?${params.toString()}`);
      setOrders(res.data);
      setTotalCount(parseInt(res.headers?.['x-total-count'] || res.data.length));
      setCurrentPage(page);
    } catch { toast.error('Failed to filter'); }
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    loadFilteredOrders(page);
  };

  // Server-side search with debounce
  const handleSearchChange = (value) => {
    setSearchSO(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      loadFilteredOrders(1, value);
    }, 400);
  };

  useEffect(() => { if (!loading) { setCurrentPage(1); loadFilteredOrders(1); } }, [filterCustomers, filterStatuses, filterOccasions, filterEvents, filterAgents, filterDistributors, filterPaymentModes, filterCities, filterStates]);

  // Client-side credit days filter (credit_days is on customer, not SO)
  const creditDaysOptions = useMemo(() => {
    const days = [...new Set(customers.filter(c => c.payment_mode === 'credit' && c.credit_days).map(c => c.credit_days))].sort();
    return days.map(d => ({ value: d, label: `${d} days` }));
  }, [customers]);
  const showCreditDaysFilter = filterPaymentModes.includes('credit');

  const displayOrders = useMemo(() => {
    let list = orders;
    if (filterCreditDays.length) {
      const custIdsWithDays = new Set(customers.filter(c => filterCreditDays.includes(c.credit_days)).map(c => c.id));
      list = list.filter(o => custIdsWithDays.has(o.customer_id));
    }
    if (filterStaff.length) {
      list = list.filter(o => filterStaff.includes(o.created_by_name));
    }
    return list;
  }, [orders, filterCreditDays, customers]);

  const getCustName = (id) => {
    const c = customers.find(c => c.id === id);
    if (!c) return 'Unknown';
    const name = c.company_name || 'Unknown';
    const loc = c.city?.toLowerCase() === 'mumbai' ? c.area : c.city;
    return loc ? `${name} (${loc})` : name;
  };
  const getCustNameShort = (id) => customers.find(c => c.id === id)?.company_name || 'Unknown';

  const downloadEventReport = async (fmt) => {
    if (!reportEventId) return;
    setDownloadingFmt(fmt);
    try {
      const res = await api.post(
        `/sales-orders/consolidated-report/export?fmt=${fmt}`,
        { event_id: reportEventId },
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const evName = events.find(e => e.id === reportEventId)?.name || 'event';
      a.download = `so-consolidated-report-${evName.replace(/\s+/g, '_')}.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (err) {
      // Blob error responses need a manual read
      let detail = 'Failed to download';
      if (err.response?.data && err.response.data.text) {
        try { const t = await err.response.data.text(); detail = JSON.parse(t).detail || detail; } catch { /* noop */ }
      }
      toast.error(detail);
    } finally { setDownloadingFmt(null); }
  };
  const getAgentName = (id) => agents.find(a => a.id === id)?.name || '';
  const getDistName = (id) => distributors.find(d => d.id === id)?.company_name || '';
  const getBrandName = (id) => brands.find(b => b.id === id)?.name || '';

  // === Item management ===
  const addProductToItems = (product) => {
    setSoItems(prev => [...prev, {
      product_id: product.id, design_code: product.design_code,
      brand_id: product.brand_id || '', fabric: product.fabric || '',
      images: product.images || [], notes: '',
      orderMode: 'set', selectedColors: [], selectedSets: [],
      setQty: {}, manualVariants: [], variants: [],
    }]);
    setAddMode(null);
  };

  const removeItem = (idx) => setSoItems(prev => prev.filter((_, i) => i !== idx));

  const toggleColor = (idx, color) => {
    setSoItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const has = item.selectedColors.includes(color);
      const newColors = has ? item.selectedColors.filter(c => c !== color) : [...item.selectedColors, color];
      return rebuildItemVariants({ ...item, selectedColors: newColors });
    }));
  };

  const toggleSet = (idx, setLabel) => {
    setSoItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const has = item.selectedSets.includes(setLabel);
      const newSets = has ? item.selectedSets.filter(s => s !== setLabel) : [...item.selectedSets, setLabel];
      return rebuildItemVariants({ ...item, selectedSets: newSets });
    }));
  };

  const updateSetQty = (idx, color, setLabel, delta) => {
    setSoItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const key = `${color}_${setLabel}`;
      const current = item.setQty[key] || 0;
      const newQty = Math.max(0, current + delta);
      const newSetQty = { ...item.setQty, [key]: newQty };
      return rebuildItemVariants({ ...item, setQty: newSetQty });
    }));
  };

  const updateManualQty = (idx, color, size, qty) => {
    setSoItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const key = `${color}_${size}`;
      const existing = item.manualVariants || {};
      return rebuildItemVariants({ ...item, manualVariants: { ...existing, [key]: Math.max(0, parseInt(qty) || 0) } });
    }));
  };

  const updateItemWsp = (idx, wsp) => {
    setSoItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      return rebuildItemVariants({ ...item, wspOverride: parseFloat(wsp) || 0 });
    }));
  };

  const setOrderMode = (idx, mode) => {
    setSoItems(prev => prev.map((item, i) => i === idx ? rebuildItemVariants({ ...item, orderMode: mode }) : item));
  };

  const rebuildItemVariants = (item) => {
    const variants = [];
    const wsp = item.wspOverride || 0;
    if (item.orderMode === 'set') {
      for (const color of item.selectedColors) {
        for (const setLabel of item.selectedSets) {
          const setDef = SIZE_SETS.find(s => s.label === setLabel);
          if (!setDef) continue;
          const qty = item.setQty[`${color}_${setLabel}`] || 0;
          for (const size of setDef.sizes) {
            const existing = variants.find(v => v.color === color && v.size === size);
            if (existing) { existing.qty_ordered += qty; }
            else { variants.push({ size, color, qty_ordered: qty, qty_dispatched: 0, wsp }); }
          }
        }
      }
    } else {
      // Manual mode
      for (const color of item.selectedColors) {
        const allSizes = enums.sizes || [];
        for (const size of allSizes) {
          const key = `${color}_${size}`;
          const qty = (item.manualVariants || {})[key] || 0;
          if (qty > 0) {
            variants.push({ size, color, qty_ordered: qty, qty_dispatched: 0, wsp });
          }
        }
      }
    }
    return { ...item, variants };
  };

  const calcItemTotal = (item) => (item.variants || []).reduce((s, v) => s + (v.qty_ordered || 0) * (v.wsp || 0), 0);
  const calcTotal = () => soItems.reduce((s, item) => s + calcItemTotal(item), 0);
  const calcTotalQty = () => soItems.reduce((s, item) => s + (item.variants || []).reduce((vs, v) => vs + (v.qty_ordered || 0), 0), 0);

  const submitSO = async () => {
    if (!selectedCustomer) { toast.error('Select a customer'); return; }
    if (soItems.length === 0) { toast.error('Add at least one product'); return; }
    if (soItems.some(item => !item.variants.length)) { toast.error('Each product needs at least one variant with qty'); return; }

    // Check for insufficient stock and warn
    const stockWarnings = [];
    for (const item of soItems) {
      const product = products.find(p => p.id === item.product_id);
      if (!product) continue;
      for (const v of item.variants) {
        if (v.qty_ordered <= 0) continue;
        const sku = (product.skus || []).find(s => s.size === v.size && s.color === v.color);
        const available = sku ? Math.max(0, (sku.current_stock || 0) - (sku.reserved_stock || 0)) : 0;
        if (v.qty_ordered > available) {
          stockWarnings.push(`${item.design_code} (${v.size}/${v.color}): Ordered ${v.qty_ordered}, Available ${available}`);
        }
      }
    }
    if (stockWarnings.length > 0) {
      const msg = `Insufficient stock for:\n${stockWarnings.join('\n')}\n\nStock will still be reserved. Continue?`;
      if (!window.confirm(msg)) return;
    }

    try {
      const payload = {
        customer_id: selectedCustomer,
        occasion,
        event_id: eventId || null,
        payment_mode: paymentMode || null,
        agent_id: selectedAgent || null,
        distributor_id: selectedDistributor || null,
        transport_name: transportName || null,
        dispatch_date: dispatchDate || null,
        items: soItems.map(item => ({
          product_id: item.product_id, design_code: item.design_code,
          brand_id: item.brand_id || null, fabric: item.fabric,
          images: item.images, notes: item.notes,
          variants: item.variants.filter(v => v.qty_ordered > 0),
        })),
        notes: soNotes,
      };
      if (editingSO) {
        await api.put(`/sales-orders/${editingSO.id}`, payload);
        toast.success(`SO ${editingSO.so_number} updated!`);
        setEditingSO(null);
      } else {
        const res = await api.post('/sales-orders', payload);
        toast.success(`SO ${res.data.so_number} created!`);
      }
      resetForm(); setIsCreateOpen(false); loadAllData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save SO'); }
  };

  const resetForm = () => {
    setSelectedCustomer(''); setCustomerSearch(''); setCustomerDropdownOpen(false);
    setOccasion('exhibition'); setSelectedAgent(''); setSelectedDistributor('');
    setEventId(''); setEventInput('');
    setCustomerHistory(null); setShowHistory(false); setPaymentMode('');
    setTransportName(''); setDispatchDate(''); setSoItems([]); setSoNotes(''); setAddMode(null); setEditingSO(null);
    setProductSearch(''); setBarcodeScanning(false);
  };

  const selectCustomer = (custId) => {
    setSelectedCustomer(custId);
    setCustomerDropdownOpen(false);
    const cust = customers.find(c => c.id === custId);
    if (cust) {
      setCustomerSearch(cust.company_name + (cust.city ? ` (${cust.city})` : ''));
      setSelectedAgent(cust.agent_id || '');
      setSelectedDistributor(cust.distributor_id || '');
      setTransportName(cust.transport_preference || '');
      setPaymentMode(cust.payment_mode || '');
      api.get(`/sales-orders/customer-history/${custId}`)
        .then(res => { setCustomerHistory(res.data); setShowHistory(true); })
        .catch(() => setCustomerHistory(null));
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers.slice(0, 20);
    const q = customerSearch.trim().toLowerCase();
    return customers.filter(c =>
      c.company_name?.toLowerCase().includes(q) ||
      c.contact_person?.toLowerCase().includes(q) ||
      c.phone?.includes(q) ||
      c.city?.toLowerCase().includes(q)
    ).slice(0, 20);
  }, [customerSearch, customers]);

  const startEditSO = (so) => {
    setSelectedCustomer(so.customer_id || '');
    const cust = customers.find(c => c.id === so.customer_id);
    setCustomerSearch(cust ? cust.company_name + (cust.city ? ` (${cust.city})` : '') : '');
    setOccasion(so.occasion || 'exhibition');
    setEventId(so.event_id || '');
    setPaymentMode(so.payment_mode || '');
    setSelectedAgent(so.agent_id || '');
    setSelectedDistributor(so.distributor_id || '');
    setTransportName(so.transport_name || '');
    setDispatchDate(so.dispatch_date || '');
    setSoNotes(so.notes || '');
    const items = (so.items || []).map(item => {
      const variants = (item.variants || []).map(v => ({
        size: v.size, color: v.color,
        qty_ordered: v.qty_ordered || 0,
        qty_dispatched: v.qty_dispatched || 0,
        wsp: v.wsp || 0,
      }));
      const colors = [...new Set(variants.map(v => v.color).filter(Boolean))];
      const wsp = variants[0]?.wsp || 0;
      return {
        id: item.id || crypto.randomUUID(),
        product_id: item.product_id || '',
        design_code: item.design_code || '',
        brand_id: item.brand_id || '',
        fabric: item.fabric || '',
        images: item.images || [],
        notes: item.notes || '',
        orderMode: 'set',
        selectedColors: colors,
        selectedSets: [],
        setQty: {},
        manualVariants: {},
        wspOverride: wsp,
        variants,
      };
    });
    setSoItems(items);
    setEditingSO(so);
    setIsCreateOpen(true);
  };

  const deleteSO = async (id) => {
    if (!window.confirm('Cancel this Sales Order?')) return;
    try { await api.delete(`/sales-orders/${id}`); toast.success('SO deleted'); loadAllData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const cloneSO = async (id) => {
    try { const res = await api.post(`/sales-orders/${id}/clone`); toast.success(`Cloned as ${res.data.so_number}!`); loadAllData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to clone'); }
  };

  const [shareDialogSO, setShareDialogSO] = useState(null);
  const [shareFormat, setShareFormat] = useState('pdf');
  const [hideColor, setHideColor] = useState(false);

  const executeShare = async (so, format, action) => {
    if (action === 'download') {
      try {
        let endpoint = format === 'pdf' ? `/sales-orders/${so.id}/pdf` : `/sales-orders/${so.id}/excel`;
        if (format === 'pdf' && hideColor) endpoint += '?hide_color=true';
        const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const ext = format === 'pdf' ? '.pdf' : '.xlsx';
        const res = await api.get(endpoint, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: mimeType }));
        const a = document.createElement('a'); a.href = url; a.download = `${so.so_number}${ext}`; a.click();
        window.URL.revokeObjectURL(url); toast.success(`${format.toUpperCase()} downloaded!`);
      } catch { toast.error(`${format.toUpperCase()} generation failed`); }
    } else if (action === 'whatsapp') {
      const cust = customers.find(c => c.id === so.customer_id);
      const msg = `Sales Order ${so.so_number}\nCustomer: ${cust?.company_name || 'N/A'}\nDate: ${fmtDate(so.so_date)}\nItems: ${so.items?.length || 0} designs\nTotal: ₹${(so.total_amount || 0).toLocaleString('en-IN')}\nStatus: ${so.status?.replace('_', ' ')}\n\nFrom Jini Nimi Fashion LLP`;
      window.open(`https://wa.me/${cust?.phone || ''}?text=${encodeURIComponent(msg)}`, '_blank');
    } else if (action === 'email') {
      const cust = customers.find(c => c.id === so.customer_id);
      const subject = `Sales Order ${so.so_number} - Jini Nimi Fashion LLP`;
      const body = `Dear ${cust?.contact_person || 'Sir/Madam'},\n\nSO Number: ${so.so_number}\nDate: ${fmtDate(so.so_date)}\nItems: ${so.items?.length || 0} designs\nTotal: ₹${(so.total_amount || 0).toLocaleString('en-IN')}\n\nRegards,\nJini Nimi Fashion LLP`;
      window.open(`mailto:${cust?.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_self');
    }
    setShareDialogSO(null);
  };

  const filteredAddProducts = useMemo(() => {
    if (!productSearch.trim()) return [];
    const q = productSearch.trim().toLowerCase();
    return products.filter(p =>
      (p.design_code && p.design_code.toLowerCase().includes(q)) ||
      (p.category && p.category.toLowerCase().includes(q)) ||
      (p.fabric && p.fabric.toLowerCase().includes(q)) ||
      (p.skus || []).some(s => s.sku_id && s.sku_id.toLowerCase().includes(q))
    );
  }, [productSearch, products]);

  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || user?.role === 'salesman';

  const soSortOptions = [
    { value: 'date_desc', label: 'Newest First' },
    { value: 'date_asc', label: 'Oldest First' },
    { value: 'name_asc', label: 'A to Z (Customer)' },
    { value: 'name_desc', label: 'Z to A (Customer)' },
    { value: 'qty_desc', label: 'Qty: High to Low' },
    { value: 'qty_asc', label: 'Qty: Low to High' },
    { value: 'amount_desc', label: 'Amount: High to Low' },
    { value: 'amount_asc', label: 'Amount: Low to High' },
  ];

  const filteredOrders = useMemo(() => {
    let list = [...displayOrders];
    list.sort((a, b) => {
      const cA = getCustName(a.customer_id), cB = getCustName(b.customer_id);
      switch (sortBy) {
        case 'date_asc': return new Date(a.so_date || a.created_at) - new Date(b.so_date || b.created_at);
        case 'name_asc': return cA.localeCompare(cB);
        case 'name_desc': return cB.localeCompare(cA);
        case 'qty_desc': return (b.total_qty || 0) - (a.total_qty || 0);
        case 'qty_asc': return (a.total_qty || 0) - (b.total_qty || 0);
        case 'amount_desc': return (b.total_amount || 0) - (a.total_amount || 0);
        case 'amount_asc': return (a.total_amount || 0) - (b.total_amount || 0);
        default: return new Date(b.so_date || b.created_at) - new Date(a.so_date || a.created_at);
      }
    });
    return list;
  }, [displayOrders, sortBy, customers]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  const statusOptions = [
    { value: 'confirmed', label: 'Confirmed' }, { value: 'partial_fulfilled', label: 'Partial Fulfilled' },
    { value: 'fulfilled', label: 'Fulfilled' }, { value: 'closed', label: 'Closed' }, { value: 'cancelled', label: 'Cancelled' },
  ];
  const custOptions = customers.map(c => ({ value: c.id, label: c.company_name }));
  const occasionOptions = [
    { value: 'exhibition', label: 'Exhibition' }, { value: 'door_to_door', label: 'Door to Door' },
    { value: 'office_visit', label: 'Office Visit' }, { value: 'online_order', label: 'Online Order' },
    { value: 'referral', label: 'Referral' },
  ];
  const paymentModeOptions = [
    { value: 'advance', label: 'Advance' },
    { value: 'cash', label: 'Cash' },
    { value: 'pdc_cheque', label: 'PDC Cheque' },
    { value: 'credit', label: 'Credit' },
  ];
  const agentOptions = agents.map(a => ({ value: a.id, label: a.name }));
  const distOptions = distributors.map(d => ({ value: d.id, label: d.name || d.company_name }));
  const cityOptions = [...new Set(customers.filter(c => !filterStates.length || filterStates.includes(c.state)).map(c => c.city).filter(Boolean))].sort().map(c => ({ value: c, label: c }));
  const stateOptions = [...new Set(customers.map(c => c.state).filter(Boolean))].sort().map(s => ({ value: s, label: s }));
  const staffOptions = [...new Set(orders.map(o => o.created_by_name).filter(Boolean))].sort().map(n => ({ value: n, label: n }));
  const hasAnyFilter = filterCustomers.length > 0 || filterStatuses.length > 0 || filterOccasions.length > 0 || filterEvents.length > 0 || filterAgents.length > 0 || filterDistributors.length > 0 || filterPaymentModes.length > 0 || filterCreditDays.length > 0 || filterCities.length > 0 || filterStates.length > 0 || filterStaff.length > 0 || searchSO;

  // === SO Detail View ===
  const renderSODetailView = (so) => {
    const totalOrdered = (so.items || []).reduce((s, i) => s + (i.variants || []).reduce((vs, v) => vs + (v.qty_ordered || 0), 0), 0);
    const totalDispatched = (so.items || []).reduce((s, i) => s + (i.variants || []).reduce((vs, v) => vs + (v.qty_dispatched || 0), 0), 0);
    return (
      <Dialog open={!!viewingSO} onOpenChange={() => setViewingSO(null)}>
        <DialogContent className="max-w-5xl z-50 max-h-[90vh] overflow-y-auto">
          <div className="space-y-4" data-testid="so-detail-view">
            <div className="text-center border-b pb-3">
              <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Jini Nimi Fashion LLP</h2>
              <p className="text-sm text-gray-600">38 Jay Gopal Ind Est, Bhawani Shankar Road, Dadar West, Mumbai 400028</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-50 p-3 rounded-lg text-sm">
              <div><span className="text-gray-500 text-xs">SO Number</span><p className="font-bold text-sky-700 font-mono">{so.so_number}</p></div>
              <div><span className="text-gray-500 text-xs">Date</span><p className="font-medium">{fmtDate(so.so_date)}</p></div>
              <div><span className="text-gray-500 text-xs">Customer</span><p className="font-semibold text-base">{getCustName(so.customer_id)}</p></div>
              <div><span className="text-gray-500 text-xs">Status</span><Badge className={STATUS_COLORS[so.status]}>{so.status?.replace('_', ' ')}</Badge>
                {so.approval_status && so.approval_status !== 'approved' && (
                  <Badge className={`ml-1 ${APPROVAL_COLORS[so.approval_status]}`}>{approvalLabel(so.approval_status)}</Badge>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              {so.occasion && <div><span className="text-gray-500">Mode of Order:</span> <strong className="capitalize">{so.occasion.replace('_',' ')}</strong></div>}
              {so.event_id && <div><span className="text-gray-500">Event:</span> <Badge className="bg-pink-100 text-pink-700 text-xs" data-testid={`so-event-badge-${so.id}`}>{events.find(e => e.id === so.event_id)?.name || 'Unknown'}</Badge></div>}
              {so.payment_mode && <div><span className="text-gray-500">Payment Mode:</span> <strong>{formatPaymentMode(so.payment_mode)}</strong></div>}
              {so.transport_name && <div><span className="text-gray-500">Transport:</span> <strong>{so.transport_name}</strong></div>}
              {so.dispatch_date && <div><span className="text-gray-500">Dispatch:</span> <strong>{fmtDate(so.dispatch_date)}</strong></div>}
              {so.agent_id && <div><span className="text-gray-500">Agent:</span> <Badge className="bg-violet-100 text-violet-700 text-xs">{getAgentName(so.agent_id)}</Badge></div>}
              {so.distributor_id && <div><span className="text-gray-500">Distributor:</span> <Badge className="bg-orange-100 text-orange-700 text-xs">{getDistName(so.distributor_id)}</Badge></div>}
            </div>
            {so.rejection_reason && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                <strong>Rejection Reason:</strong> {so.rejection_reason}
              </div>
            )}
            <div className="bg-sky-50 p-3 rounded-lg border border-sky-200">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-600">Dispatch Progress</span>
                <span className="font-semibold">{totalDispatched}/{totalOrdered} pcs ({totalOrdered > 0 ? Math.round(totalDispatched/totalOrdered*100) : 0}%)</span>
              </div>
              <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-sky-500 rounded-full transition-all" style={{ width: `${totalOrdered > 0 ? Math.min(100, (totalDispatched/totalOrdered)*100) : 0}%` }}></div>
              </div>
              {canManage && (so.status === 'partial_fulfilled' || so.status === 'confirmed') && totalDispatched > 0 && totalDispatched < totalOrdered && (
                <div className="mt-2 flex justify-end">
                  <Button size="sm" variant="outline" className="border-orange-400 text-orange-700 hover:bg-orange-50" onClick={async () => {
                    const pending = totalOrdered - totalDispatched;
                    if (!window.confirm(`Close balance of ${pending} pcs? This will mark the SO as closed and release reserved stock. This cannot be undone.`)) return;
                    try { await api.post(`/sales-orders/${so.id}/close-balance`); toast.success(`Balance closed! ${pending} pcs written off.`); setViewingSO(null); loadAllData(); }
                    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  }} data-testid="close-balance-so-btn">
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    Close Balance ({totalOrdered - totalDispatched} pcs)
                  </Button>
                </div>
              )}
            </div>
            {(so.items || []).map((item, idx) => {
              const sv = sortColorWise(item.variants || []);
              return (
                <div key={idx} className="border rounded-lg p-3" data-testid={`so-detail-item-${idx}`}>
                  <div className="flex items-start gap-3 mb-2">
                    {item.images?.[0] && <img src={`${API_BASE}${item.images[0]}`} alt={item.design_code} className="w-16 h-16 rounded-lg object-cover border" />}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">Design #{item.design_code}</span>
                      {item.brand_id && <Badge className="bg-indigo-100 text-indigo-700 text-xs">{getBrandName(item.brand_id)}</Badge>}
                      {item.fabric && <Badge variant="outline" className="text-xs">{item.fabric}</Badge>}
                      <Badge className={`text-xs ${STATUS_COLORS[item.status] || 'bg-gray-100'}`}>{item.status?.replace('_',' ')}</Badge>
                    </div>
                  </div>
                  {sv.length > 0 && (
                    <table className="w-full text-xs border rounded">
                      <thead className="bg-gray-50"><tr>
                        <th className="p-1.5 text-left">Color</th><th className="p-1.5 text-left">Size</th>
                        <th className="p-1.5 text-right">Ordered</th><th className="p-1.5 text-right">WSP</th>
                        <th className="p-1.5 text-right">Dispatched</th><th className="p-1.5 text-right">Pending</th>
                        <th className="p-1.5 text-right">Amount</th>
                      </tr></thead>
                      <tbody>{sv.map((v, vi) => {
                        const pending = Math.max(0, (v.qty_ordered || 0) - (v.qty_dispatched || 0));
                        const done = (v.qty_dispatched || 0) >= (v.qty_ordered || 0);
                        return (
                          <tr key={vi} className={`border-t ${done ? 'bg-green-50' : (v.qty_dispatched || 0) > 0 ? 'bg-amber-50' : ''}`}>
                            <td className="p-1.5 font-medium">{v.color}</td><td className="p-1.5">{v.size}</td>
                            <td className="p-1.5 text-right">{v.qty_ordered}</td>
                            <td className="p-1.5 text-right">₹{v.wsp}</td>
                            <td className="p-1.5 text-right"><span className={done ? 'text-green-600 font-semibold' : ''}>{v.qty_dispatched || 0}</span></td>
                            <td className="p-1.5 text-right"><span className={pending > 0 ? 'text-red-600' : 'text-green-600'}>{pending}</span></td>
                            <td className="p-1.5 text-right font-medium">₹{((v.qty_ordered||0)*(v.wsp||0)).toLocaleString('en-IN')}</td>
                          </tr>);
                      })}</tbody>
                    </table>
                  )}
                </div>
              );
            })}
            <div className="border-t pt-3 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                <span>Ordered: <strong>{totalOrdered} pcs</strong></span>
                <span className="mx-3 text-gray-300">|</span>
                <span className="text-green-600">Dispatched: <strong>{totalDispatched} pcs</strong></span>
                <span className="mx-3 text-gray-300">|</span>
                <span className={so.status === 'closed' ? 'text-orange-600' : 'text-red-600'}>{so.status === 'closed' ? 'Short Closed' : 'Pending'}: <strong>{Math.max(0, totalOrdered - totalDispatched)} pcs</strong></span>
              </div>
              <div className="text-lg font-bold text-gray-900">₹{(so.total_amount || 0).toLocaleString('en-IN')}</div>
            </div>
            {so.notes && <p className="text-sm text-gray-600 italic">Notes: {so.notes}</p>}
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // === RENDER ===
  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="so-management-container">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Sales Orders</h1>
          <p className="text-gray-600 mt-1">Create and manage sales orders for customers</p>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="so-filters">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[180px]">
              <Label className="text-xs mb-1 block">Search SO</Label>
              <Input value={searchSO} onChange={e => handleSearchChange(e.target.value)} placeholder="Search SO#, customer, design, city, state, agent..." className="h-9 text-sm" data-testid="search-so-input" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Sort By</Label>
              <SortDropdown value={sortBy} onChange={setSortBy} options={soSortOptions} testId="so-sort" />
            </div>
            <div className="min-w-[160px]">
              <Label className="text-xs mb-1 block">Customer</Label>
              <MultiSelectFilter label="All Customers" options={custOptions} selected={filterCustomers} onChange={setFilterCustomers} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">Status</Label>
              <MultiSelectFilter label="All Status" options={statusOptions} selected={filterStatuses} onChange={setFilterStatuses} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">Mode of Order</Label>
              <MultiSelectFilter label="All Modes" options={occasionOptions} selected={filterOccasions} onChange={setFilterOccasions} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">Event</Label>
              <MultiSelectFilter label="All Events" options={events.map(ev => ({ value: ev.id, label: ev.name }))} selected={filterEvents} onChange={setFilterEvents} testId="so-filter-event" />
            </div>
            <div className="min-w-[150px]">
              <Label className="text-xs mb-1 block">Payment Mode</Label>
              <MultiSelectFilter label="All Payments" options={paymentModeOptions} selected={filterPaymentModes} onChange={(v) => { setFilterPaymentModes(v); if (!v.includes('credit')) setFilterCreditDays([]); }} testId="so-filter-payment-mode" />
            </div>
            {showCreditDaysFilter && creditDaysOptions.length > 0 && (
              <div className="min-w-[130px]">
                <Label className="text-xs mb-1 block">Credit Days</Label>
                <MultiSelectFilter label="All Days" options={creditDaysOptions} selected={filterCreditDays} onChange={setFilterCreditDays} testId="so-filter-credit-days" />
              </div>
            )}
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">Agent</Label>
              <MultiSelectFilter label="All Agents" options={agentOptions} selected={filterAgents} onChange={setFilterAgents} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">Distributor</Label>
              <MultiSelectFilter label="All Distributors" options={distOptions} selected={filterDistributors} onChange={setFilterDistributors} />
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs mb-1 block">City</Label>
              <MultiSelectFilter label="All Cities" options={cityOptions} selected={filterCities} onChange={setFilterCities} />
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs mb-1 block">State</Label>
              <MultiSelectFilter label="All States" options={stateOptions} selected={filterStates} onChange={setFilterStates} />
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs mb-1 block">Staff</Label>
              <MultiSelectFilter label="All Staff" options={staffOptions} selected={filterStaff} onChange={setFilterStaff} testId="so-filter-staff" />
            </div>
            {hasAnyFilter && (
              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { setFilterCustomers([]); setFilterStatuses([]); setFilterOccasions([]); setFilterEvents([]); setFilterAgents([]); setFilterDistributors([]); setFilterPaymentModes([]); setFilterCreditDays([]); setFilterCities([]); setFilterStates([]); setFilterStaff([]); setSearchSO(''); }}>Clear All</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* SO List */}
      <Card data-testid="so-list">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Sales Orders ({totalCount})</CardTitle>
            {canManage && (
              <Button size="sm" className="bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-700 hover:to-cyan-700 shadow-sm"
                onClick={() => navigate('/create-sales-order')} data-testid="create-so-button">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                Create SO
              </Button>
            )}
            <Button
              size="sm" variant="outline"
              className="border-pink-300 text-pink-700 hover:bg-pink-50"
              onClick={() => setEventReportPickerOpen(true)}
              disabled={!events.length}
              data-testid="report-by-event-btn"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2a4 4 0 014-4h6m0 0l-3-3m3 3l-3 3M5 7h14M5 11h6m-6 4h4" /></svg>
              Report by Event
            </Button>
          </div>
          <CardDescription>All sales orders with customers</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Drafts Section */}
          {drafts.length > 0 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="so-drafts-section">
              <p className="text-xs font-semibold text-amber-800 mb-2">Drafts ({drafts.length})</p>
              <div className="flex flex-wrap gap-2">
                {drafts.map(d => (
                  <div key={d.id} className="flex items-center gap-2 bg-white border border-amber-200 rounded-lg px-3 py-1.5 text-sm">
                    <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                    <button className="font-medium text-amber-900 hover:text-blue-700 hover:underline"
                      onClick={() => navigate('/create-sales-order', { state: { draftId: d.id } })}
                      data-testid={`so-draft-${d.id}`}>
                      {d.title || 'Untitled Draft'}
                    </button>
                    <span className="text-xs text-gray-400">{new Date(d.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    <button className="text-gray-400 hover:text-red-500 text-xs ml-1" onClick={async () => {
                      await api.delete(`/drafts/${d.id}`);
                      setDrafts(prev => prev.filter(x => x.id !== d.id));
                    }}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Sales Orders</h3>
              <p className="text-gray-600">Create your first SO or adjust filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="so-table">
                <thead>
                  <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wider">
                    <th className="px-2 py-3 w-10"><input type="checkbox" className="w-4 h-4 rounded" onChange={e => { if (e.target.checked) { const ids = (filteredOrders || orders).map(x => x.id); bulk.selectAll(ids); } else { bulk.clearSelection(); }}} data-testid="so-select-all" /></th>
                    <th className="px-4 py-3 text-left font-medium">SO #</th>
                    <th className="px-4 py-3 text-left font-medium">Date</th>
                    <th className="px-4 py-3 text-left font-medium">Customer</th>
                    <th className="px-4 py-3 text-center font-medium">Items</th>
                    <th className="px-4 py-3 text-right font-medium">Ordered</th>
                    <th className="px-4 py-3 text-right font-medium">Dispatched</th>
                    <th className="px-4 py-3 text-right font-medium">Amount</th>
                    <th className="px-4 py-3 text-center font-medium">Progress</th>
                    <th className="px-4 py-3 text-center font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">By</th>
                    <th className="px-4 py-3 text-center font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredOrders.map(so => {
                    const totalOrd = (so.items||[]).reduce((s,i) => s + (i.variants||[]).reduce((vs,v) => vs + (v.qty_ordered||0), 0), 0);
                    const totalDisp = (so.items||[]).reduce((s,i) => s + (i.variants||[]).reduce((vs,v) => vs + (v.qty_dispatched||0), 0), 0);
                    const pct = totalOrd > 0 ? Math.round(totalDisp / totalOrd * 100) : 0;
                    return (
                      <tr key={so.id} className={`hover:bg-sky-50/50 cursor-pointer transition-colors group ${bulk.isSelected(so.id) ? 'bg-blue-50' : ''}`} onClick={() => navigate(`/sales-order/${so.id}`)} data-testid={`so-row-${so.id}`}>
                        <td className="px-2 py-3" onClick={e => e.stopPropagation()}><input type="checkbox" className="w-4 h-4 rounded" checked={bulk.isSelected(so.id)} onChange={() => bulk.toggleSelect(so.id)} /></td>
                        <td className="px-4 py-3">
                          <span className="font-bold text-sky-700 font-mono group-hover:underline" data-testid={`so-number-link-${so.id}`}>{so.so_number}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {fmtDate(so.so_date)}
                          {so.created_at && <span className="block text-[10px] text-gray-400">{new Date(so.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900 max-w-[220px]">
                          <div className="truncate">{getCustName(so.customer_id)}</div>
                          {so.event_id && (
                            <span
                              className="inline-block mt-1 text-[10px] font-medium bg-pink-50 text-pink-700 border border-pink-200 px-1.5 py-0.5 rounded truncate max-w-full"
                              title={events.find(e => e.id === so.event_id)?.name || 'Event'}
                              data-testid={`so-row-event-${so.id}`}
                            >
                              🎟 {events.find(e => e.id === so.event_id)?.name || 'Event'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">{so.items?.length || 0}</td>
                        <td className="px-4 py-3 text-right font-medium">{totalOrd}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={totalDisp >= totalOrd && totalOrd > 0 ? 'text-green-600 font-semibold' : totalDisp > 0 ? 'text-amber-600 font-medium' : 'text-gray-400'}>{totalDisp}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">₹{(so.total_amount || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 justify-center">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : pct > 0 ? 'bg-amber-500' : 'bg-gray-300'}`} style={{ width: `${Math.min(100, pct)}%` }}></div>
                            </div>
                            <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={`${STATUS_COLORS[so.status] || 'bg-gray-100'} text-[11px]`}>{so.status?.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                          {so.approval_status && so.approval_status !== 'approved' && (
                            <Badge className={`ml-1 ${APPROVAL_COLORS[so.approval_status]} text-[10px]`}>{approvalLabel(so.approval_status)}</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 truncate max-w-[100px]">{so.created_by_name || '-'}</td>
                        <td className="px-2 py-3 text-center" onClick={e => e.stopPropagation()}>
                          <div className="flex flex-col items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
                              onClick={() => navigate('/create-sales-order', { state: { repeatSO: so } })}
                              data-testid={`so-repeat-${so.id}`}
                              title="Start a new order with these items"
                            >
                              ↻ Repeat
                            </Button>
                            {so.approval_status === 'approved' ? (
                              totalDisp < totalOrd ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs border-sky-300 text-sky-700 hover:bg-sky-50"
                                  onClick={() => navigate('/create-sales-entry', { state: { fromSO: so } })}
                                  data-testid={`so-create-se-${so.id}`}
                                  title="Create Sales Entry against this SO"
                                >
                                  + Create SE
                                </Button>
                              ) : (
                                <span className="text-[10px] text-emerald-600 font-medium" data-testid={`so-se-complete-${so.id}`}>Fully dispatched</span>
                              )
                            ) : (
                              <span className="text-[10px] text-amber-600" data-testid={`so-se-blocked-${so.id}`} title="SO awaits admin approval">Pending approval</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 py-3" data-testid="so-pagination">
          <button
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={currentPage <= 1}
            onClick={() => goToPage(currentPage - 1)}
            data-testid="so-page-prev"
          >Prev</button>
          {(() => {
            const pages = [];
            const maxVisible = 7;
            let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
            let end = Math.min(totalPages, start + maxVisible - 1);
            if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
            if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
            for (let i = start; i <= end; i++) pages.push(i);
            if (end < totalPages) { if (end < totalPages - 1) pages.push('...'); pages.push(totalPages); }
            return pages.map((p, i) => p === '...' ? (
              <span key={`dots-${i}`} className="px-2 py-1.5 text-gray-400">...</span>
            ) : (
              <button key={p}
                className={`px-3 py-1.5 text-sm rounded-md border ${p === currentPage ? 'bg-emerald-600 text-white border-emerald-600 font-bold' : 'border-gray-300 hover:bg-gray-100'}`}
                onClick={() => goToPage(p)}
                data-testid={`so-page-${p}`}
              >{p}</button>
            ));
          })()}
          <button
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={currentPage >= totalPages}
            onClick={() => goToPage(currentPage + 1)}
            data-testid="so-page-next"
          >Next</button>
          <span className="text-xs text-gray-500 ml-3">
            Showing {((currentPage - 1) * PAGE_SIZE) + 1}-{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
          </span>
        </div>
      )}


      {/* Share Dialog */}
      {shareDialogSO && (
        <Dialog open={!!shareDialogSO} onOpenChange={() => setShareDialogSO(null)}>
          <DialogContent className="max-w-sm z-50">
            <DialogHeader>
              <DialogTitle>Share {shareDialogSO.so_number}</DialogTitle>
              <DialogDescription>Choose format and sharing method</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Format</Label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => { setShareFormat('pdf'); }} className={`flex-1 p-3 rounded-lg border-2 text-center transition-colors ${shareFormat === 'pdf' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-blue-300'}`} data-testid="so-share-format-pdf">
                    <svg className="w-6 h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                    <span className="text-sm font-medium">PDF</span>
                  </button>
                  <button type="button" onClick={() => { setShareFormat('excel'); setHideColor(false); }} className={`flex-1 p-3 rounded-lg border-2 text-center transition-colors ${shareFormat === 'excel' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-green-300'}`} data-testid="so-share-format-excel">
                    <svg className="w-6 h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    <span className="text-sm font-medium">Excel</span>
                  </button>
                </div>
              </div>
              {shareFormat === 'pdf' && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid="hide-color-option">
                  <input type="checkbox" id="hideColor" checked={hideColor} onChange={e => setHideColor(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-testid="hide-color-checkbox" />
                  <label htmlFor="hideColor" className="text-sm font-medium text-blue-800 cursor-pointer">Hide Color Column</label>
                  <span className="text-xs text-blue-600 ml-auto">Merges color rows into one per design</span>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Share via</Label>
                <div className="flex flex-col gap-2">
                  <Button onClick={() => executeShare(shareDialogSO, shareFormat, 'download')} className="justify-start" data-testid="so-share-action-download">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Download {shareFormat.toUpperCase()}
                  </Button>
                  <Button variant="outline" className="justify-start text-green-700 border-green-300 hover:bg-green-50" onClick={() => executeShare(shareDialogSO, shareFormat, 'whatsapp')} data-testid="so-share-action-whatsapp">
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </Button>
                  <Button variant="outline" className="justify-start text-blue-700 border-blue-300 hover:bg-blue-50" onClick={() => executeShare(shareDialogSO, shareFormat, 'email')} data-testid="so-share-action-email">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    Email
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      <BulkActionBar selectedCount={bulk.selectedCount} selectedIds={bulk.selectedIds} docType="SO" label="sales orders"
        onComplete={() => { bulk.clearSelection(); loadAllData(); }} onClear={bulk.clearSelection}
        extraActions={[{
          label: 'Generate Report',
          testId: 'bulk-generate-report-btn',
          onClick: () => setReportDialogOpen(true),
        }, {
          label: 'Print Barcodes',
          testId: 'bulk-print-barcodes-btn',
          className: 'text-green-300 hover:text-green-200 hover:bg-green-900/30 gap-1',
          onClick: async () => {
            try {
              const res = await api.post('/sales-orders/barcode-excel', {
                so_ids: bulk.selectedIds,
              }, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
              const a = document.createElement('a'); a.href = url; a.download = `barcodes-bulk-${bulk.selectedCount}SOs.xlsx`; a.click();
              window.URL.revokeObjectURL(url);
              toast.success('Barcode Excel downloaded!');
            } catch { toast.error('Failed to generate barcode Excel'); }
          },
        }, {
          label: 'Bulk Approve',
          testId: 'bulk-approve-btn',
          className: 'text-emerald-300 hover:text-emerald-200 hover:bg-emerald-900/30',
          onClick: async () => {
            if (!window.confirm(`Approve ${bulk.selectedCount} SO(s)?`)) return;
            try { await api.post('/sales-orders/bulk-status', { ids: bulk.selectedIds, action: 'approve' }); toast.success(`${bulk.selectedCount} SO(s) approved`); bulk.clearSelection(); loadAllData(); } catch { toast.error('Failed'); }
          },
        }, {
          label: 'Bulk Close',
          testId: 'bulk-close-btn',
          className: 'text-amber-300 hover:text-amber-200 hover:bg-amber-900/30',
          onClick: async () => {
            if (!window.confirm(`Close ${bulk.selectedCount} SO(s)?`)) return;
            try { await api.post('/sales-orders/bulk-status', { ids: bulk.selectedIds, action: 'close' }); toast.success(`${bulk.selectedCount} SO(s) closed`); bulk.clearSelection(); loadAllData(); } catch { toast.error('Failed'); }
          },
        }, {
          label: 'Bulk Cancel',
          testId: 'bulk-cancel-btn',
          className: 'text-red-300 hover:text-red-200 hover:bg-red-900/30',
          onClick: async () => {
            if (!window.confirm(`Cancel ${bulk.selectedCount} SO(s)? This cannot be undone.`)) return;
            try { await api.post('/sales-orders/bulk-status', { ids: bulk.selectedIds, action: 'cancel' }); toast.success(`${bulk.selectedCount} SO(s) cancelled`); bulk.clearSelection(); loadAllData(); } catch { toast.error('Failed'); }
          },
        }]} />
      <ConsolidatedReportDialog
        open={reportDialogOpen}
        onOpenChange={(o) => { setReportDialogOpen(o); if (!o) setReportEventId(''); }}
        selectedIds={reportEventId ? null : bulk.selectedIds}
        eventId={reportEventId || null}
        eventName={reportEventId ? events.find(e => e.id === reportEventId)?.name : null}
      />

      {/* Event picker dialog for "Report by Event" */}
      <Dialog open={eventReportPickerOpen} onOpenChange={setEventReportPickerOpen}>
        <DialogContent className="max-w-md" data-testid="event-report-picker">
          <DialogHeader>
            <DialogTitle>Generate Report by Event</DialogTitle>
            <DialogDescription>
              Pick an event — view the consolidated report or download it directly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Label className="text-xs font-semibold text-gray-600">Event</Label>
            <select
              value={reportEventId}
              onChange={(e) => setReportEventId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="event-report-picker-select"
            >
              <option value="">— Select event —</option>
              {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>

            {/* Direct download */}
            <div className="border-t pt-3 mt-2">
              <p className="text-xs text-gray-500 mb-2">Download directly</p>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline"
                  className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  disabled={!reportEventId || downloadingFmt !== null}
                  onClick={() => downloadEventReport('xlsx')}
                  data-testid="event-report-download-xlsx"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  {downloadingFmt === 'xlsx' ? 'Downloading…' : 'Excel'}
                </Button>
                <Button
                  size="sm" variant="outline"
                  className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                  disabled={!reportEventId || downloadingFmt !== null}
                  onClick={() => downloadEventReport('pdf')}
                  data-testid="event-report-download-pdf"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  {downloadingFmt === 'pdf' ? 'Downloading…' : 'PDF'}
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => { setEventReportPickerOpen(false); setReportEventId(''); }} data-testid="event-report-picker-cancel">Cancel</Button>
              <Button
                className="bg-pink-600 hover:bg-pink-700 text-white"
                disabled={!reportEventId}
                onClick={() => { setEventReportPickerOpen(false); setReportDialogOpen(true); }}
                data-testid="event-report-picker-go"
              >
                View Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesOrderManagement;
